import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DropDown, ErrorMessage } from 'anf-core-react';

function AddressDropDown({
  name,
  value,
  label,
  placeholder,
  onChange,
  onBlur,
  error,
  errorMessage,
  hasValidationError,
  isInvalid,
  optionList,
  isRequired,
  isDisabled,
}) {
  // Extract label text from either string or object format
  const labelText = typeof label === 'string' ? label : label?.value || '';

  // Extract error text from either string or object format
  const errorText = error?.value || errorMessage || '';

  // Determine if field is invalid
  const fieldIsInvalid = isInvalid || hasValidationError;

  // Add state management like the working AddressField
  const [fieldValue, setFieldValue] = useState(value);

  // Update local state when value prop changes
  useEffect(() => {
    setFieldValue(value);
  }, [value]);

  const handleOnChange = (event) => {
    setFieldValue(event.target.value);
    if (onChange) onChange(event);
  };

  const handleOnBlur = (event) => {
    if (onBlur) onBlur(event);
  };

  const processOptions = (option) => {
    if (typeof option === 'string') {
      return { label: option, value: option };
    }
    // Handle different option data structures like the working AddressField
    // eslint-disable-next-line max-len
    const optionLabel = option.text?.value || option.text || option.label || option.name || option.optionValue || option.key;
    const optionValue = option.value || option.optionValue || option.id || option.key;

    return { label: optionLabel, value: optionValue };
  };

  const options = optionList ? optionList.map(processOptions) : [];

  return (
    <DropDown
      id={`${name}-dropdown`}
      name={name}
      value={fieldValue || ''}
      label={labelText}
      placeholder={placeholder}
      onChange={handleOnChange}
      onBlur={handleOnBlur}
      isInvalid={fieldIsInvalid}
      isRequired={isRequired}
      isDisabled={isDisabled}
      options={options}
    >
      {fieldIsInvalid && errorText && (
        <ErrorMessage>
          {errorText}
        </ErrorMessage>
      )}
    </DropDown>
  );
}

AddressDropDown.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
  ]).isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.shape({
    key: PropTypes.string,
    value: PropTypes.string,
  }),
  errorMessage: PropTypes.string,
  hasValidationError: PropTypes.bool,
  isInvalid: PropTypes.bool,
  optionList: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string,
        text: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({
            key: PropTypes.string,
            value: PropTypes.string,
          }),
        ]),
        label: PropTypes.string,
        optionValue: PropTypes.string,
        name: PropTypes.string,
        id: PropTypes.string,
        key: PropTypes.string,
      }),
    ]),
  ),
  isRequired: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

AddressDropDown.defaultProps = {
  value: '',
  placeholder: '',
  onChange: null,
  onBlur: null,
  error: null,
  errorMessage: '',
  hasValidationError: false,
  isInvalid: false,
  optionList: [],
  isRequired: false,
  isDisabled: false,
};

export default AddressDropDown;
